import { classNames } from "./utils/classNames";

export const TextIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg {...props} viewBox="0 0 24 24" fill="none">
    <path
      d="M.75 12.749h10.5m4.5 4.5h7.5m-7.5 4.5V13.5a3.75 3.75 0 1 1 7.5 0v8.25m-22.5-.001V7.5a5.25 5.25 0 0 1 10.5 0v14.25"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const PlayIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg
    {...props}
    // Some manipulation to make the icon appear roughly in the right size and position as the others
    viewBox="-2 0 28 24"
    fill="currentColor"
    stroke="currentColor"
    strokeWidth="1"
  >
    <path d="M2.33801 3.25499H3.08801V3.25492L2.33801 3.25499ZM2.33801 20.745L3.08801 20.7451V20.745H2.33801ZM4.54701 22.067L4.19283 21.4059L4.19251 21.4061L4.54701 22.067ZM20.87 13.322L21.2242 13.9831L21.2244 13.983L20.87 13.322ZM20.87 10.678L21.2244 10.017L21.2242 10.0169L20.87 10.678ZM4.54701 1.93299L4.19251 2.59392L4.19283 2.59409L4.54701 1.93299ZM1.58801 3.25499V20.745H3.08801V3.25499H1.58801ZM1.58801 20.7449C1.58794 21.5362 2.00357 22.2694 2.68258 22.6758L3.45287 21.3887C3.22653 21.2532 3.08799 21.0088 3.08801 20.7451L1.58801 20.7449ZM2.68258 22.6758C3.3616 23.0822 4.20417 23.102 4.90151 22.7279L4.19251 21.4061C3.96006 21.5307 3.67921 21.5241 3.45287 21.3887L2.68258 22.6758ZM4.9012 22.7281L21.2242 13.9831L20.5158 12.6609L4.19283 21.4059L4.9012 22.7281ZM21.2244 13.983C21.9552 13.5912 22.4113 12.8292 22.4113 12H20.9113C20.9113 12.2764 20.7592 12.5304 20.5156 12.661L21.2244 13.983ZM22.4113 12C22.4113 11.1708 21.9552 10.4088 21.2244 10.017L20.5156 11.339C20.7592 11.4696 20.9113 11.7236 20.9113 12H22.4113ZM21.2242 10.0169L4.9012 1.27189L4.19283 2.59409L20.5158 11.3391L21.2242 10.0169ZM4.90151 1.27206C4.20417 0.898028 3.3616 0.91782 2.68258 1.32418L3.45287 2.6113C3.67921 2.47584 3.96006 2.46924 4.19251 2.59392L4.90151 1.27206ZM2.68258 1.32418C2.00357 1.73055 1.58794 2.46374 1.58801 3.25506L3.08801 3.25492C3.08799 2.99115 3.22653 2.74675 3.45287 2.6113L2.68258 1.32418Z" />
  </svg>
);

export const PlaySolidIcon = (
  props: React.ComponentProps<"svg">
): JSX.Element => (
  <svg
    {...props}
    width="29"
    height="35"
    viewBox="0 0 29 35"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Different to above. Sharp edges. */}
    <path
      d="M0 33.2546V1.79766C0 1.01362 0.861209 0.534544 1.52736 0.948018L27.5917 17.1259C28.2313 17.5229 28.2195 18.4575 27.5701 18.8382L1.50572 34.1173C0.839075 34.5081 0 34.0274 0 33.2546Z"
      fill="currentColor"
    />
  </svg>
);

export const CircleIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <circle strokeWidth={2} cx="12" cy="12" r="9" />
  </svg>
);

interface FractionalCircleIcon extends React.ComponentProps<"svg"> {
  fraction: number;
}

export const FractionalCircleIcon = (
  props: FractionalCircleIcon
): JSX.Element => {
  const { fraction, className, ...rest } = props;
  const radius = 9;
  const circumference = 2 * radius * Math.PI;
  const arcLength = circumference * fraction;
  return (
    <svg
      {...rest}
      className={classNames(className, "-rotate-90 transition-all")}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle
        cx="12"
        cy="12"
        r={radius}
        strokeDasharray={`${arcLength} 9999`} // arclength followed by big gap
      />
      <circle cx="12" cy="12" r={radius} className="opacity-10" />
    </svg>
  );
};

export const LoadingIcon = (
  props: React.ComponentProps<"svg">
): JSX.Element => {
  const { className, ...rest } = props;
  return (
    <svg
      className={classNames("animate-spin", className)}
      {...rest}
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  );
};

export const Lissajous1Icon = (
  props: React.ComponentProps<"svg">
): JSX.Element => (
  <svg {...props} width="80" height="80" viewBox="0 0 80 80" fill="none">
    <path
      d="M39.7869 71.2437C22.7862 71.2437 8.95529 57.4125 8.95529 40.4118C8.95529 23.4111 22.7862 9.58017 39.7869 9.58017C56.7877 9.58017 70.6188 23.4122 70.6188 40.4118C70.6188 57.4115 56.7877 71.2437 39.7869 71.2437ZM39.7869 12.1536C24.2056 12.1536 11.5287 24.8302 11.5287 40.4131C11.5287 55.9961 24.2053 68.6713 39.7883 68.6713C55.3712 68.6713 68.0478 55.995 68.0478 40.4131C68.0478 24.8313 55.3688 12.1536 39.7869 12.1536Z"
      fill="currentColor"
    />
  </svg>
);

export const Lissajous2Icon = (
  props: React.ComponentProps<"svg">
): JSX.Element => (
  <svg {...props} width="80" height="80" viewBox="0 0 80 80" fill="none">
    <path
      d="M60.8711 70.8361C54.8761 70.8361 50.2243 62.1582 42.5176 47.7813C41.376 45.6521 40.1686 43.4124 38.8952 41.0624C37.1738 37.9024 35.6269 34.9723 34.1308 32.1397C28.0463 20.6151 23.6503 12.2885 19.6947 12.2885C15.296 12.2877 11.7163 24.9198 11.7163 40.4468H9.14337C9.14337 25.6459 12.4445 9.71429 19.6942 9.71429C25.2018 9.71429 29.4191 17.7026 36.4067 30.9372C37.8983 33.7634 39.4431 36.686 41.1552 39.8313C42.4356 42.1814 43.6458 44.4261 44.7858 46.5654C51.5382 59.1615 56.4167 68.2615 60.8711 68.2615C64.1575 68.2615 67.8198 56.839 67.8198 40.4468H70.3933C70.393 40.7507 70.2843 70.8361 60.8711 70.8361Z"
      fill="currentColor"
    />
    <path
      d="M18.6648 70.8361C9.25152 70.8361 9.14285 40.7507 9.14285 40.4458H11.7163C11.7163 53.6034 14.57 68.2613 18.6648 68.2613C23.1189 68.2613 27.9976 59.1604 34.7501 46.5643C35.894 44.4303 37.1041 42.1856 38.3807 39.8302C40.093 36.686 41.6378 33.7634 43.1294 30.9372C50.117 17.7026 54.3344 9.71429 59.8419 9.71429C67.0914 9.71429 70.3933 25.6451 70.3933 40.4468H67.8198C67.8198 24.9188 64.2409 12.2877 59.8419 12.2877C55.8859 12.2877 51.4895 20.6143 45.4059 32.1389C43.9098 34.9723 42.3628 37.9024 40.6414 41.0616C39.363 43.4054 38.1555 45.6451 37.0191 47.7805C29.3115 62.1582 24.6598 70.8361 18.6648 70.8361Z"
      fill="currentColor"
    />
  </svg>
);

export const Lissajous5Icon = (
  props: React.ComponentProps<"svg">
): JSX.Element => (
  <svg {...props} viewBox="0 0 41 40" fill="none">
    <g clipPath="url(#clip0_859_1195)">
      <path
        d="M3.08272 39.9296C2.74114 39.9292 2.40276 39.8639 2.08559 39.7371C1.70427 39.5871 1.18344 39.2291 0.867319 38.3744C-0.22785 35.4135 1.79063 28.3262 6.01689 20.2401C0.613845 10.3472 -0.688573 2.21146 1.82517 0.401581C3.75238 -0.986852 6.93105 1.33945 13.2782 9.29585C18.0854 3.64408 23.2682 0.247329 27.5799 0.247329C31.148 0.247329 34.3716 2.05636 36.6567 5.34136C39.1207 8.88322 40.423 14.0191 40.423 20.1935C40.423 33.6522 33.4427 39.9225 28.7252 39.9225C24.5802 39.9225 19.5699 38.7288 13.1509 30.9278L12.9965 31.1156C8.07786 37.0935 5.344 39.9296 3.08272 39.9296ZM14.2063 29.6349C20.2974 37.1419 24.9243 38.2862 28.7252 38.2862C32.2337 38.2862 38.7868 32.979 38.7868 20.1932C38.7868 6.67832 32.7498 1.88297 27.5799 1.88297C23.7143 1.88297 18.8538 5.20725 14.3192 10.6144C16.2882 13.1344 18.5404 16.1512 21.1259 19.7128L21.4698 20.1869L21.1315 20.6659C21.1058 20.7018 18.7034 24.0945 14.2063 29.6352V29.6349ZM6.97897 21.9455C2.83737 30.1202 1.68006 35.8557 2.40171 37.8068C2.48349 38.027 2.58644 38.176 2.68447 38.2146C4.17027 38.7997 7.1278 35.6736 11.7332 30.0761C11.86 29.9219 11.9853 29.7696 12.1089 29.619C11.1776 28.4115 10.2181 27.0658 9.22806 25.5663C8.42006 24.3425 7.67036 23.1356 6.97897 21.9455ZM7.87857 20.2213C8.70825 21.6981 9.61716 23.1858 10.5935 24.6645C11.48 26.0073 12.3375 27.2199 13.1705 28.315C16.5329 24.1612 18.6664 21.278 19.4537 20.1957C17.1061 16.9681 15.0563 14.2302 13.2588 11.9246C11.3536 14.3621 9.5259 17.16 7.87857 20.2213ZM3.01059 1.67437C2.87157 1.67437 2.80351 1.71331 2.78116 1.72923C2.25626 2.10766 1.84278 4.32137 2.97198 8.65057C3.75679 11.6587 5.12439 15.034 6.94968 18.5123C8.58329 15.5887 10.3622 12.9263 12.2118 10.5947C10.4086 8.32768 8.8796 6.53389 7.56719 5.14257C4.69466 2.09598 3.46894 1.67437 3.01059 1.67437Z"
        fill="currentColor"
      />
      <path
        d="M37.9123 39.9296C35.651 39.9296 32.9173 37.094 27.9985 31.1156L27.8441 30.9278C21.4251 38.7288 16.4149 39.9225 12.2697 39.9225C7.55229 39.9225 0.572021 33.6522 0.572021 20.1935C0.572021 14.019 1.87444 8.88284 4.3384 5.34132C6.62424 2.05649 9.84693 0.247288 13.415 0.247288C17.7271 0.247288 22.9096 3.64404 27.7168 9.29581C34.0638 1.33992 37.2433 -0.985539 39.1698 0.40154C41.6836 2.21141 40.3811 10.3471 34.9781 20.2404C39.2044 28.3262 41.2228 35.4134 40.1277 38.3747C39.8115 39.2291 39.2907 39.5874 38.9094 39.7374C38.5922 39.8641 38.2538 39.9293 37.9123 39.9296ZM13.415 1.8831C8.24515 1.88327 2.20834 6.67862 2.20834 20.1935C2.20834 32.9793 8.76107 38.2865 12.2699 38.2865C16.0705 38.2865 20.6977 37.1422 26.7889 29.6352C22.2919 24.0945 19.8894 20.7018 19.8636 20.6654L19.526 20.1871L19.8699 19.713C22.4548 16.1506 24.7069 13.1345 26.6766 10.6145C22.1413 5.20688 17.2806 1.88327 13.415 1.88327V1.8831ZM28.8861 29.6179C29.0103 29.7692 29.1355 29.9216 29.2618 30.0751C33.8674 35.6722 36.8213 38.7989 38.3107 38.2135C38.4085 38.1749 38.5117 38.0273 38.5933 37.8058C39.3149 35.8547 38.1578 30.1186 34.016 21.9444C33.3235 23.1333 32.5738 24.3401 31.7669 25.5649C30.7769 27.0662 29.817 28.4112 28.8861 29.6186V29.6179ZM21.5413 20.195C22.3281 21.2773 24.4622 24.1605 27.8244 28.3143C28.6567 27.2191 29.5149 26.0066 30.4015 24.6637C31.3778 23.1851 32.2861 21.6974 33.1164 20.2206C31.4691 17.1593 29.6414 14.3621 27.7362 11.9238C25.9387 14.2302 23.8889 16.9681 21.5413 20.1957V20.195ZM28.7831 10.5945C30.6328 12.9261 32.4117 15.5885 34.0455 18.5121C35.8706 15.0334 37.2382 11.6585 38.023 8.65036C39.1522 4.32116 38.7394 2.10745 38.2138 1.72902C38.1182 1.65977 37.1725 1.17026 33.428 5.14236C32.1159 6.53385 30.5866 8.32764 28.7831 10.5947V10.5945Z"
        fill="currentColor"
      />
    </g>
    <defs>
      <clipPath id="clip0_859_1195">
        <rect width="40" height="40" fill="white" transform="translate(0.5)" />
      </clipPath>
    </defs>
  </svg>
);

export const CommandIcon = (
  props: React.ComponentProps<"svg">
): JSX.Element => (
  <svg {...props} viewBox="0 0 24 24" fill="none">
    <path
      d="M23 19.3333C23 21.3586 21.3586 23 19.3333 23C17.3081 23 15.6667 21.3586 15.6667 19.3333V4.66667C15.6667 2.64144 17.3081 1 19.3333 1C21.3586 1 23 2.64144 23 4.66667C23 6.69189 21.3586 8.33333 19.3333 8.33333H4.66667C2.64144 8.33333 1 6.69189 1 4.66667C1 2.64144 2.64144 1 4.66667 1C6.69189 1 8.33333 2.64144 8.33333 4.66667V19.3333C8.33333 21.3586 6.69189 23 4.66667 23C2.64144 23 1 21.3586 1 19.3333C1 17.3081 2.64144 15.6667 4.66667 15.6667H19.3333C21.3586 15.6667 23 17.3081 23 19.3333Z"
      stroke="currentColor"
      strokeMiterlimit={10}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ReturnIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg {...props} viewBox="0 0 24 25" fill="none">
    <path
      d="M5.25 24.247L0.75 19.747M0.75 19.747L5.25 15.247M0.75 19.747H18.75C21.2353 19.747 23.25 17.7323 23.25 15.247V6.24698C23.25 3.7617 21.2353 1.74698 18.75 1.74698H10.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    ></path>
  </svg>
);

export const TwitterIcon = (
  props: React.ComponentProps<"svg">
): JSX.Element => (
  <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M24 6.32327L21.745 5.35683L22.9262 2.77965L20.1772 3.6924C19.3016 2.87622 18.1526 2.41663 16.9557 2.40381C14.2894 2.40971 12.1294 4.56971 12.1235 7.23602V8.30985C8.32215 9.09374 5.00403 7.02126 1.92215 3.47763C1.38523 6.34117 1.92215 8.48882 3.53289 9.92059L0 9.38367C0.28169 11.731 2.20365 13.5399 4.56376 13.679L1.61074 14.7528C2.68456 16.9005 4.63893 17.2333 7.24832 17.4374C5.11551 18.8921 2.58102 19.643 0 19.585C13.702 25.6736 21.745 16.7286 21.745 8.84676V7.95549L24 6.32327Z"
      fill="currentColor"
    />
  </svg>
);

// Icons not really tested out.
export const FacebookIcon = (
  props: React.ComponentProps<"svg">
): JSX.Element => (
  <svg {...props} role="img" viewBox="0 0 24 24">
    <title>Facebook icon</title>
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);
export const LinkedinIcon = (
  props: React.ComponentProps<"svg">
): JSX.Element => (
  <svg {...props} role="img" viewBox="0 0 24 24">
    <title>LinkedIn icon</title>
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);
export const GithubIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg {...props} viewBox="0 0 24 24">
    <title>GitHub icon</title>
    <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
  </svg>
);
export const YoutubeIcon = (
  props: React.ComponentProps<"svg">
): JSX.Element => (
  <svg {...props} viewBox="0 0 24 24">
    <title>YouTube icon</title>
    <path d="M23.499 6.203a3.008 3.008 0 00-2.089-2.089c-1.87-.501-9.4-.501-9.4-.501s-7.509-.01-9.399.501a3.008 3.008 0 00-2.088 2.09A31.258 31.26 0 000 12.01a31.258 31.26 0 00.523 5.785 3.008 3.008 0 002.088 2.089c1.869.502 9.4.502 9.4.502s7.508 0 9.399-.502a3.008 3.008 0 002.089-2.09 31.258 31.26 0 00.5-5.784 31.258 31.26 0 00-.5-5.808zm-13.891 9.4V8.407l6.266 3.604z" />
  </svg>
);

// Platform grouping icons
export const AnnotateIcon = (
  props: React.ComponentProps<"svg">
): JSX.Element => (
  <svg {...props} width="25" height="25" viewBox="0 0 25 25" fill="none">
    <path
      d="M17.25 20.25H23.25"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    <path
      d="M20.25 17.25V23.25"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    <path
      d="M14.25 20.25H4.25C3.42157 20.25 2.75 19.5784 2.75 18.75V4.25C2.75 3.42157 3.42157 2.75 4.25 2.75H18.75C19.5784 2.75 20.25 3.42157 20.25 4.25V14.25"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ShapeCubeIcon = (
  props: React.ComponentProps<"svg">
): JSX.Element => (
  // Necker cube
  <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16.629 23.25C17.0265 23.2499 17.4078 23.092 17.689 22.811L22.811 17.689C23.092 17.4078 23.2499 17.0265 23.25 16.629V2.25C23.25 1.42157 22.5784 0.75 21.75 0.75H7.371C6.97345 0.750085 6.5922 0.907982 6.311 1.189L1.189 6.311C0.907982 6.5922 0.750085 6.97345 0.75 7.371V21.75C0.75 22.5784 1.42157 23.25 2.25 23.25H16.629Z"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M17.25 23.115V6.75"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.75 17.25H23.115"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M1.18945 22.811L6.75045 17.25"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M17.25 6.74996L22.811 1.18896"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M17.2498 6.75H0.884766"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.75 0.88501V17.25"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const CheckCircleIcon = (
  // Slightly more sparse than heroicons
  props: React.ComponentProps<"svg">
): JSX.Element => (
  <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M8.5 13L11.5 15L15.5 9M23 12C23 18.0751 18.0751 23 12 23C5.92487 23 1 18.0751 1 12C1 5.92487 5.92487 1 12 1C18.0751 1 23 5.92487 23 12Z"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ArrowUpRightIcon = (
  // Slightly more sparse than heroicons
  props: React.ComponentProps<"svg">
): JSX.Element => (
  <svg
    {...props}
    width="12"
    height="12"
    viewBox="0 -1 13 13" // edited to make a bit smaller
    fill="none"
  >
    <path
      d="M2.5 9.5L9.5 2.5M9.5 2.5H2.90038M9.5 2.5V9.09962"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const FeedbackIcon = (
  props: React.ComponentProps<"svg">
): JSX.Element => (
  <svg {...props} width="17" height="16" viewBox="0 0 17 16" fill="none">
    <path
      d="M1.48047 6V10"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15.8809 1V15"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M1.48047 7L15.8805 2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M1.48047 9L15.8805 14"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4.28125 9.97266V9.99999C4.33718 11.8788 5.51478 13.5208 7.22856 14.1096C8.94234 14.6983 10.8266 14.1082 11.9446 12.6327"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const CodeFileIcon = (
  props: React.ComponentProps<"svg">
): JSX.Element => (
  <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M18.045 0.753052L18.0451 0.00305176H18.045V0.753052ZM19.093 1.18005L18.5689 1.71655L18.5693 1.71697L19.093 1.18005ZM22.048 4.06205L22.572 3.52547L22.5717 3.52513L22.048 4.06205ZM22.5 5.13605L21.75 5.13564V5.13605H22.5ZM14.8253 8.47272C14.5324 8.17983 14.0576 8.17983 13.7647 8.47272C13.4718 8.76561 13.4718 9.24049 13.7647 9.53338L14.8253 8.47272ZM18.045 12.7531L18.5753 13.2834C18.8682 12.9905 18.8682 12.5156 18.5753 12.2227L18.045 12.7531ZM13.7647 15.9727C13.4718 16.2656 13.4718 16.7405 13.7647 17.0334C14.0576 17.3263 14.5324 17.3263 14.8253 17.0334L13.7647 15.9727ZM10.3253 9.53338C10.6182 9.24049 10.6182 8.76561 10.3253 8.47272C10.0324 8.17983 9.55756 8.17983 9.26467 8.47272L10.3253 9.53338ZM6.045 12.7531L5.51467 12.2227C5.22178 12.5156 5.22178 12.9905 5.51467 13.2834L6.045 12.7531ZM9.26467 17.0334C9.55756 17.3263 10.0324 17.3263 10.3253 17.0334C10.6182 16.7405 10.6182 16.2656 10.3253 15.9727L9.26467 17.0334ZM21.75 21.7531C21.75 22.1673 21.4142 22.5031 21 22.5031V24.0031C22.2426 24.0031 23.25 22.9957 23.25 21.7531H21.75ZM21 22.5031H3V24.0031H21V22.5031ZM3 22.5031C2.58579 22.5031 2.25 22.1673 2.25 21.7531H0.75C0.75 22.9957 1.75736 24.0031 3 24.0031V22.5031ZM2.25 21.7531V2.25305H0.75V21.7531H2.25ZM2.25 2.25305C2.25 1.83884 2.58579 1.50305 3 1.50305V0.00305176C1.75736 0.00305176 0.75 1.01041 0.75 2.25305H2.25ZM3 1.50305H18.045V0.00305176H3V1.50305ZM18.0449 1.50305C18.2408 1.50307 18.4288 1.5797 18.5689 1.71655L19.6171 0.643552C19.1968 0.232997 18.6326 0.00312036 18.0451 0.00305176L18.0449 1.50305ZM18.5693 1.71697L21.5243 4.59897L22.5717 3.52513L19.6167 0.643131L18.5693 1.71697ZM21.524 4.59864C21.6686 4.73987 21.7501 4.9335 21.75 5.13564L23.25 5.13647C23.2503 4.53004 23.0059 3.94916 22.572 3.52547L21.524 4.59864ZM21.75 5.13605V21.7531H23.25V5.13605H21.75ZM13.7647 9.53338L17.5147 13.2834L18.5753 12.2227L14.8253 8.47272L13.7647 9.53338ZM17.5147 12.2227L13.7647 15.9727L14.8253 17.0334L18.5753 13.2834L17.5147 12.2227ZM9.26467 8.47272L5.51467 12.2227L6.57533 13.2834L10.3253 9.53338L9.26467 8.47272ZM5.51467 13.2834L9.26467 17.0334L10.3253 15.9727L6.57533 12.2227L5.51467 13.2834Z"
      fill="currentColor"
    />
  </svg>
);

export const FlaskIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M6.726 0.75H17.226M5.301 12.75H18.651M14.226 17.25H17.226M15.726 15.75V18.75M15.726 3.75H12.726M15.726 6.75H12.726M15.726 8.25V0.75H8.226V8.25L1.489 18.615C0.889844 19.5367 0.842849 20.7124 1.36652 21.679C1.89019 22.6456 2.90063 23.2484 4 23.25H19.948C21.0481 23.2499 22.0599 22.6477 22.5845 21.6808C23.1092 20.7139 23.0625 19.5373 22.463 18.615L15.726 8.25ZM6.726 19.125C6.93272 19.1256 7.1 19.2933 7.1 19.5C7.1 19.7071 6.93311 19.875 6.726 19.875C6.51889 19.875 6.351 19.7071 6.351 19.5C6.351 19.2929 6.51889 19.125 6.726 19.125ZM9.726 16.125C9.93311 16.125 10.101 16.2929 10.101 16.5C10.101 16.7071 9.93311 16.875 9.726 16.875C9.51889 16.875 9.351 16.7071 9.351 16.5C9.351 16.2929 9.51889 16.125 9.726 16.125Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const MoneyGraphArrowIncreaseIcon = (
  props: React.ComponentProps<"svg">
): JSX.Element => (
  <svg {...props} viewBox="0 0 14 14">
    <g>
      <polyline
        points="9.5 3.5 13.5 3.5 13.5 7.5"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      ></polyline>
      <path
        d="M13.5,3.5,7.85,9.15a.5.5,0,0,1-.7,0l-2.3-2.3a.5.5,0,0,0-.7,0L.5,10.5"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
    </g>
  </svg>
);

export const BrainHeadIcon = (
  props: React.ComponentProps<"svg">
): JSX.Element => (
  <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M19.2494 23.25V16.985C22.0773 14.3652 23.011 10.2802 21.602 6.69208C20.1931 3.10391 16.7293 0.745664 12.8744 0.750006C5.73544 0.750006 4.19944 6.62801 1.64944 13.23C1.56093 13.4607 1.59158 13.7202 1.7314 13.924C1.87123 14.1277 2.10233 14.2496 2.34944 14.25H4.24944V17.25C4.24944 18.9069 5.59259 20.25 7.24944 20.25H8.74944V23.25"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.31701 7.70003C9.12158 7.59762 8.98099 7.41454 8.93247 7.19931C8.88396 6.98407 8.93241 6.75838 9.06501 6.58203C10.6137 4.5387 13.4116 3.91257 15.6835 5.10089C17.9554 6.2892 19.037 8.94448 18.242 11.382C18.1736 11.5919 18.0162 11.761 17.8118 11.8442C17.6073 11.9275 17.3766 11.9164 17.181 11.814L9.31701 7.70003Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const DollarIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M16.5 4.5C16.9142 4.5 17.25 4.16421 17.25 3.75C17.25 3.33579 16.9142 3 16.5 3V4.5ZM10.8 3.75V4.5V3.75ZM7.5 19.5C7.08579 19.5 6.75 19.8358 6.75 20.25C6.75 20.6642 7.08579 21 7.5 21V19.5ZM11.25 3.75C11.25 4.16421 11.5858 4.5 12 4.5C12.4142 4.5 12.75 4.16421 12.75 3.75H11.25ZM12.75 0.75C12.75 0.335786 12.4142 0 12 0C11.5858 0 11.25 0.335786 11.25 0.75H12.75ZM12.75 20.25C12.75 19.8358 12.4142 19.5 12 19.5C11.5858 19.5 11.25 19.8358 11.25 20.25H12.75ZM11.25 23.25C11.25 23.6642 11.5858 24 12 24C12.4142 24 12.75 23.6642 12.75 23.25H11.25ZM16.5 3H10.8V4.5H16.5V3ZM10.8 3C8.56325 3 6.75 4.81325 6.75 7.05H8.25C8.25 5.64167 9.39167 4.5 10.8 4.5V3ZM6.75 7.05C6.75 8.55543 7.4491 9.67077 8.39946 10.5419C9.32249 11.388 10.534 12.0496 11.6386 12.6572C12.784 13.2871 13.8225 13.863 14.587 14.5638C15.3241 15.2395 15.75 15.9804 15.75 16.95H17.25C17.25 15.4446 16.5509 14.3292 15.6005 13.4581C14.6775 12.612 13.466 11.9504 12.3614 11.3428C11.216 10.7129 10.1775 10.137 9.41304 9.4362C8.6759 8.76048 8.25 8.01957 8.25 7.05H6.75ZM15.75 16.95C15.75 18.3583 14.6083 19.5 13.2 19.5V21C15.4368 21 17.25 19.1868 17.25 16.95H15.75ZM13.2 19.5H7.5V21H13.2V19.5ZM12.75 3.75V0.75H11.25V3.75H12.75ZM11.25 20.25V23.25H12.75V20.25H11.25Z"
      fill="currentColor"
    />
  </svg>
);

export const ClockIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 12H11.25C11.25 12.1989 11.329 12.3896 11.4696 12.5303L12 12ZM12.75 8.25C12.75 7.83579 12.4142 7.5 12 7.5C11.5858 7.5 11.25 7.83579 11.25 8.25H12.75ZM16.1566 17.2183C16.4495 17.5112 16.9243 17.5112 17.2173 17.2184C17.5102 16.9255 17.5102 16.4507 17.2174 16.1577L16.1566 17.2183ZM12 23.25C18.2132 23.25 23.25 18.2132 23.25 12H21.75C21.75 17.3848 17.3848 21.75 12 21.75V23.25ZM23.25 12C23.25 5.7868 18.2132 0.75 12 0.75V2.25C17.3848 2.25 21.75 6.61522 21.75 12H23.25ZM12 0.75C5.7868 0.75 0.75 5.7868 0.75 12H2.25C2.25 6.61522 6.61522 2.25 12 2.25V0.75ZM0.75 12C0.75 18.2132 5.7868 23.25 12 23.25V21.75C6.61522 21.75 2.25 17.3848 2.25 12H0.75ZM12.75 12V8.25H11.25V12H12.75ZM11.4696 12.5303L16.1566 17.2183L17.2174 16.1577L12.5304 11.4697L11.4696 12.5303Z"
      fill="currentColor"
    />
  </svg>
);

export const PencilIcon = (props: React.ComponentProps<"svg">): JSX.Element => (
  <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M16.0938 3.34251L20.6572 7.906M3.57417 15.8614L8.14203 20.4214M21.0572 2.94252C20.4456 2.33354 19.616 1.99423 18.753 2.00007C17.89 2.00592 17.0651 2.35643 16.4618 2.97363L3.57329 15.8623L2 22L8.13759 20.4258L21.0261 7.53711C21.6434 6.9341 21.9941 6.10928 21.9999 5.2463C22.0058 4.38333 21.6663 3.55383 21.0572 2.94252Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const RefreshIcon = (
  props: React.ComponentProps<"svg">
): JSX.Element => (
  <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M5.25 14.248V18.748H0.75M18.75 9.74796V5.24796H23.25M19.032 5.24496C22.2304 8.57707 22.6526 13.6959 20.0434 17.507C17.4343 21.3181 12.5095 22.7763 8.246 21M4.967 18.751C1.77575 15.4174 1.35778 10.3033 3.96537 6.49579C6.57296 2.68823 11.4923 1.22944 15.754 2.99996"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const LightningIcon = (
  props: React.ComponentProps<"svg">
): JSX.Element => (
  <svg {...props} width="14" height="14" viewBox="0 0 14 14" fill="none">
    <polygon
      points="8 0.5 8 5.5 11.5 5.5 6 13.5 6 8.5 2.5 8.5 8 0.5"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    ></polygon>
  </svg>
);
